import {
  IRecruitmentRetentionPlacement,
  IRetentionBarChartMonth,
  IQuickviewMetric,
} from 'app/models/MyCandidates/RecruitmentRetention';
import {
  getAmnStartOfWeek,
  getAmnEndOfWeek,
  getFirstOfMonth,
  getLastOfMonth,
} from 'utils/dates/dateExtraction';
import { startOfMonth, endOfMonth, addMonths } from 'date-fns';

const FY_MONTH_ORDER = ['Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb'];

const getDateOnly = (d: string | Date | undefined): Date | null => {
  if (!d) return null;
  const date = typeof d === 'string' ? new Date(d) : d;
  return isNaN(date.getTime()) ? null : date;
};

const toDateOnlyStr = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function computeBarChartData(
  placements: IRecruitmentRetentionPlacement[],
  fyStartYear: number,
): IRetentionBarChartMonth[] {
  const byMonth: Record<string, { booked: number; rebooked: number }> = {};
  FY_MONTH_ORDER.forEach(m => {
    byMonth[m] = { booked: 0, rebooked: 0 };
  });

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const fyStart = new Date(fyStartYear, 3, 1);
  const fyEnd = new Date(fyStartYear + 1, 2, 31, 23, 59, 59, 999);

  placements.forEach(p => {
    const endDate = getDateOnly((p as any).currentEndDate ?? (p as any).CurrentEndDate ?? p.currentEndDate);
    if (!endDate || endDate < fyStart || endDate > fyEnd) return;
    const monthLabel = monthNames[endDate.getMonth()];
    const rebooked = ((p.rebooked ?? (p as any).Rebooked) ?? '').toString().toUpperCase() === 'Y' ? 1 : 0;
    const booked = rebooked ? 0 : 1;
    if (byMonth[monthLabel]) {
      byMonth[monthLabel].booked += booked;
      byMonth[monthLabel].rebooked += rebooked;
    }
  });

  return FY_MONTH_ORDER.map(monthLabel => {
    const { booked, rebooked } = byMonth[monthLabel];
    const total = booked + rebooked;
    const retentionPercent = total > 0 ? (rebooked / total) * 100 : 0;
    return {
      month: monthLabel,
      monthLabel,
      booked,
      rebooked,
      total,
      retentionPercent,
    };
  });
}

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/**
 * Filter placements to those whose currentEndDate falls in the given bar chart month.
 * Month is the chart label e.g. "Mar", "Apr", "Jan", "Feb".
 */
export function filterPlacementsByBarChartMonth(
  placements: IRecruitmentRetentionPlacement[],
  monthLabel: string | null,
): IRecruitmentRetentionPlacement[] {
  if (!monthLabel || !MONTH_LABELS.includes(monthLabel as any)) return placements;

  const monthIndex = MONTH_LABELS.indexOf(monthLabel as (typeof MONTH_LABELS)[number]);

  return placements.filter(p => {
    const endDate = getDateOnly((p as any).currentEndDate ?? (p as any).CurrentEndDate ?? p.currentEndDate);
    if (!endDate) return false;
    return endDate.getMonth() === monthIndex;
  });
}

/**
 * Quickview metrics per SQL rules (use date-only for comparisons):
 * - NEW APPLICATIONS (Today)   = LastApplicationDate >= today AND < tomorrow
 * - NEW APPLICATIONS (Week)    = LastApplicationDate >= StartOfWeek AND < EndOfWeek
 * - CALL BACK (Today)          = CallBackDate >= today AND < tomorrow
 * - CALL BACK (Week)           = CallBackDate >= StartOfWeek AND < EndOfWeek
 * - NON-CONTACTED (20 days)    = DATEDIFF(day, RecruiterLastAttempt, GETDATE()) >= 20 OR RecruiterLastAttempt IS NULL
 * - NON-CONTACTED (30 days)    = DATEDIFF(day, RecruiterLastAttempt, GETDATE()) >= 30 OR RecruiterLastAttempt IS NULL
 * - AVAILABLE TO START (Week)  = AvailableToStart >= StartOfWeek AND < EndOfWeek
 * - AVAILABLE TO START (Month) = AvailableToStart >= StartOfMonth AND <= EndOfMonth
 * - AVAILABLE TO START (Next)  = AvailableToStart >= StartOfNextMonth AND <= EndOfNextMonth
 * - REACHING 365 DAYS         = DATEDIFF(day, RecruiterLastAttempt, GETDATE()) >= 365 (excludes NULL)
 * - HOT CANDIDATES             = (no source - requires separate definition)
 */
export function computeQuickviewMetrics(placements: IRecruitmentRetentionPlacement[]): IQuickviewMetric[] {
  const now = new Date();
  const todayStr = toDateOnlyStr(now);
  const tomorrowStr = toDateOnlyStr(new Date(now.getTime() + 86400000));
  const weekStart = getAmnStartOfWeek();
  const weekEnd = getAmnEndOfWeek();
  const monthStart = startOfMonth(now);
  const monthEnd = endOfMonth(now);
  const nextMonthStart = startOfMonth(addMonths(now, 1));
  const nextMonthEnd = endOfMonth(addMonths(now, 1));

  const toDate = (d: string | Date | undefined) => (d ? new Date(d) : null);

  let newAppsToday = 0;
  let newAppsWeek = 0;
  let callBackToday = 0;
  let callBackWeek = 0;
  let nonContacted20 = 0;
  let nonContacted30 = 0;
  let availStartWeek = 0;
  let availStartMonth = 0;
  let availStartNext = 0;
  let reaching365 = 0;

  placements.forEach(p => {
    const lastApp = toDate(p.lastApplicationDate);
    if (lastApp) {
      const dStr = toDateOnlyStr(lastApp);
      if (dStr >= todayStr && dStr < tomorrowStr) newAppsToday++;
      if (lastApp >= weekStart && lastApp < weekEnd) newAppsWeek++;
    }

    const cb = toDate(p.callBackDate);
    if (cb) {
      const dStr = toDateOnlyStr(cb);
      if (dStr >= todayStr && dStr < tomorrowStr) callBackToday++;
      if (cb >= weekStart && cb < weekEnd) callBackWeek++;
    }

    const lastAttempt = toDate(p.recruiterLastAttempt);
    const daysSince = lastAttempt ? Math.floor((now.getTime() - lastAttempt.getTime()) / 86400000) : 999;
    if (daysSince >= 20) nonContacted20++;
    if (daysSince >= 30) nonContacted30++;
    if (lastAttempt && daysSince >= 365) reaching365++;

    const availRaw = toDate(p.availableToStart ?? p.availableToStartDate);
    const avail = availRaw ? new Date(availRaw.getFullYear(), availRaw.getMonth(), availRaw.getDate()) : null;
    if (avail) {
      const weekStartOnly = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate());
      const weekEndOnly = new Date(weekEnd.getFullYear(), weekEnd.getMonth(), weekEnd.getDate());
      if (avail >= weekStartOnly && avail <= weekEndOnly) availStartWeek++;
      if (avail >= monthStart && avail <= monthEnd) availStartMonth++;
      if (avail >= nextMonthStart && avail <= nextMonthEnd) availStartNext++;
    }
  });

  const metrics: IQuickviewMetric[] = [
    { key: 'newApplicationsToday', count: newAppsToday, label: 'NEW APPLICATIONS (Today)', color: 'blue' },
    { key: 'newApplicationsWeek', count: newAppsWeek, label: 'NEW APPLICATIONS (This week)', color: 'blue' },
    { key: 'callBackToday', count: callBackToday, label: 'CALL BACK (Today)', color: 'blue' },
    { key: 'callBackWeek', count: callBackWeek, label: 'CALL BACK (This week)', color: 'blue' },
    { key: 'nonContacted20', count: nonContacted20, label: 'NON-CONTACTED (20 days)', color: 'orange' },
    { key: 'nonContacted30', count: nonContacted30, label: 'NON-CONTACTED (30 days)', color: 'orange' },
    { key: 'availableToStartWeek', count: availStartWeek, label: 'AVAILABLE TO START (This week)', color: 'green' },
    { key: 'availableToStartMonth', count: availStartMonth, label: 'AVAILABLE TO START (This month)', color: 'green' },
    { key: 'availableToStartNext', count: availStartNext, label: 'AVAILABLE TO START (Next month)', color: 'green' },
    { key: 'reaching365', count: reaching365, label: 'REACHING 365 DAYS', color: 'orange' },
    { key: 'hotCandidates', count: 0, label: 'HOT CANDIDATES', color: 'red' },
  ];

  return metrics;
}

const toStr = (v: unknown): string => {
  if (v == null) return '';
  if (typeof v === 'object' && v !== null && 'value' in v) return toStr((v as any).value);
  return String(v).trim();
};

const getDiscipline = (p: IRecruitmentRetentionPlacement): string => {
  const d = (p.discipline ?? (p as any).Discipline ?? '').toString().trim();
  if (d) return d;
  const skillset = (p.skillset ?? (p as any).Skillset ?? '').toString();
  const parts = skillset.split(/[,-]/).map(s => s.trim()).filter(Boolean);
  return parts[0] ?? '';
};

const getSpecialty = (p: IRecruitmentRetentionPlacement): string => {
  const s = (p.specialty ?? (p as any).Specialty ?? '').toString().trim();
  if (s) return s;
  const skillset = (p.skillset ?? (p as any).Skillset ?? '').toString();
  const parts = skillset.split(/[,-]/).map(x => x.trim()).filter(Boolean);
  return parts[1] ?? '';
};

const toFilterValues = (arr: unknown[]): string[] => {
  if (!Array.isArray(arr) || arr.length === 0) return [];
  return arr
    .map(item => (item && typeof item === 'object' && 'value' in item ? (item as any).value : String(item)))
    .filter(v => v && v !== 'All');
};

/**
 * Extract activityTypeId:activityStatusId pairs from placement status tree.
 * Tree: parent = activity type, leaf = activity status. Supports nodes with activityTypeId/activityStatusId or id/value.
 * Matches placements by placementActivityTypeID and placementActivityStatusID.
 */
const extractTreeActivityPairs = (nodes: any[]): string[] => {
  if (!Array.isArray(nodes)) return [];
  const out: string[] = [];
  const walk = (n: any, parentTypeId?: string) => {
    if (!n) return;
    const typeId = n.activityTypeId ?? n.activityTypeID ?? n.id ?? n.value;
    const currentTypeId = parentTypeId ?? (typeId != null ? String(typeId) : undefined);
    if (Array.isArray(n.children) && n.children.length > 0) {
      n.children.forEach((c: any) => walk(c, currentTypeId));
    } else {
      // Leaf: use activityStatusId/activityStatusID if present, else id/value
      const statusId = n.activityStatusId ?? n.activityStatusID ?? n.id ?? n.value;
      if (currentTypeId != null && statusId != null) {
        out.push(`${currentTypeId}:${String(statusId)}`);
      }
    }
  };
  nodes.forEach(n => walk(n));
  return Array.from(new Set(out));
};

const getFyBoundsForFilter = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const fyStartYear = month >= 3 ? year : year - 1;
  const fyStart = new Date(fyStartYear, 3, 1);
  const fyEnd = new Date(fyStartYear + 1, 2, 31, 23, 59, 59, 999);
  return { fyStart, fyEnd };
};

const isBookedOutOfRange = (p: IRecruitmentRetentionPlacement): boolean => {
  const endDate = getDateOnly((p.currentEndDate ?? (p as any).CurrentEndDate ?? '') as string);
  if (!endDate) return true;
  const { fyStart, fyEnd } = getFyBoundsForFilter();
  return endDate < fyStart || endDate > fyEnd;
};

export function filterPlacementsByGridFilters(
  placements: IRecruitmentRetentionPlacement[],
  filters: {
    discipline: unknown[];
    specialty: unknown[];
    states: string;
    candidateStatus: unknown[];
    rebooked: string;
    bookedOutOfRange: string;
    placementStatus: unknown[];
  },
): IRecruitmentRetentionPlacement[] {
  const disciplineFilter = toFilterValues((filters?.discipline as unknown[]) ?? []);
  const specialtyFilter = toFilterValues((filters?.specialty as unknown[]) ?? []);
  const stateFilter = toStr(filters?.states);
  const candidateStatusFilter = toFilterValues((filters?.candidateStatus as unknown[]) ?? []);
  const rebookedFilter = toStr(filters?.rebooked);
  const bookedOutOfRangeFilter = toStr(filters?.bookedOutOfRange);
  const placementStatusFilter = extractTreeActivityPairs((filters?.placementStatus as any[]) ?? []);

  return placements.filter(p => {
    if (disciplineFilter.length > 0) {
      const pDiscipline = getDiscipline(p);
      if (!pDiscipline || !disciplineFilter.includes(pDiscipline)) return false;
    }
    if (specialtyFilter.length > 0) {
      const pSpecialty = getSpecialty(p);
      if (!pSpecialty || !specialtyFilter.includes(pSpecialty)) return false;
    }
    if (stateFilter && stateFilter !== 'all') {
      const state = (p.state ?? (p as any).State ?? '').toString().trim();
      if (state !== stateFilter) return false;
    }
    if (candidateStatusFilter.length > 0) {
      const pStatus = (p.candidateStatus ?? (p as any).CandidateStatus ?? '').toString().trim();
      if (!pStatus || pStatus === '-' || !candidateStatusFilter.includes(pStatus)) return false;
    }
    if (rebookedFilter === 'Y') {
      const isRebooked = ((p.rebooked ?? (p as any).Rebooked) ?? '').toString().toUpperCase() === 'Y';
      if (!isRebooked) return false;
    }
    if (rebookedFilter === 'N') {
      const isRebooked = ((p.rebooked ?? (p as any).Rebooked) ?? '').toString().toUpperCase() === 'Y';
      if (isRebooked) return false;
    }
    if (bookedOutOfRangeFilter === 'Y') {
      if (!isBookedOutOfRange(p)) return false;
    }
    if (bookedOutOfRangeFilter === 'N') {
      if (isBookedOutOfRange(p)) return false;
    }
    if (placementStatusFilter.length > 0) {
      const pTypeId =
        p.placementActivityTypeID ??
        (p as any).PlacementActivityTypeID ??
        (p as any).placementActivityTypeId ??
        (p as any).activityTypeId ??
        (p as any).ActivityTypeId;
      const pStatusId =
        p.placementActivityStatusID ??
        (p as any).PlacementActivityStatusID ??
        (p as any).placementActivityStatusId ??
        (p as any).activityStatusId ??
        (p as any).ActivityStatusId;
      const rowKey = pTypeId != null && pStatusId != null ? `${pTypeId}:${pStatusId}` : null;
      if (rowKey && !placementStatusFilter.includes(rowKey)) return false;
      if (!rowKey) return false; // No activity type/status on placement - exclude when filter is active
    }
    return true;
  });
}

export function filterPlacementsByQuickviewKey(
  placements: IRecruitmentRetentionPlacement[],
  key: string | null,
): IRecruitmentRetentionPlacement[] {
  if (!key) return placements;

  const now = new Date();
  const todayStr = toDateOnlyStr(now);
  const tomorrowStr = toDateOnlyStr(new Date(now.getTime() + 86400000));
  const weekStart = getAmnStartOfWeek();
  const weekEnd = getAmnEndOfWeek();
  const monthStart = startOfMonth(now);
  const monthEnd = endOfMonth(now);
  const nextMonthStart = startOfMonth(addMonths(now, 1));
  const nextMonthEnd = endOfMonth(addMonths(now, 1));

  const toDate = (d: string | Date | undefined) => (d ? new Date(d) : null);

  return placements.filter(p => {
    switch (key) {
      case 'newApplicationsToday': {
        const d = toDate(p.lastApplicationDate);
        if (!d) return false;
        const dStr = toDateOnlyStr(d);
        return dStr >= todayStr && dStr < tomorrowStr;
      }
      case 'newApplicationsWeek': {
        const d = toDate(p.lastApplicationDate);
        return d ? d >= weekStart && d < weekEnd : false;
      }
      case 'callBackToday': {
        const d = toDate(p.callBackDate);
        if (!d) return false;
        const dStr = toDateOnlyStr(d);
        return dStr >= todayStr && dStr < tomorrowStr;
      }
      case 'callBackWeek': {
        const d = toDate(p.callBackDate);
        return d ? d >= weekStart && d < weekEnd : false;
      }
      case 'nonContacted20': {
        const lastAttempt = toDate(p.recruiterLastAttempt);
        const daysSince = lastAttempt ? Math.floor((now.getTime() - lastAttempt.getTime()) / 86400000) : 999;
        return daysSince >= 20;
      }
      case 'nonContacted30': {
        const lastAttempt = toDate(p.recruiterLastAttempt);
        const daysSince = lastAttempt ? Math.floor((now.getTime() - lastAttempt.getTime()) / 86400000) : 999;
        return daysSince >= 30;
      }
      case 'availableToStartWeek': {
        const d = toDate(p.availableToStart ?? p.availableToStartDate);
        if (!d) return false;
        const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const weekStartOnly = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate());
        const weekEndOnly = new Date(weekEnd.getFullYear(), weekEnd.getMonth(), weekEnd.getDate());
        return dOnly >= weekStartOnly && dOnly <= weekEndOnly;
      }
      case 'availableToStartMonth': {
        const d = toDate(p.availableToStart ?? p.availableToStartDate);
        return d ? d >= monthStart && d <= monthEnd : false;
      }
      case 'availableToStartNext': {
        const d = toDate(p.availableToStart ?? p.availableToStartDate);
        return d ? d >= nextMonthStart && d <= nextMonthEnd : false;
      }
      case 'reaching365': {
        const lastAttempt = toDate(p.recruiterLastAttempt);
        const daysSince = lastAttempt ? Math.floor((now.getTime() - lastAttempt.getTime()) / 86400000) : 0;
        return daysSince >= 365;
      }
      case 'hotCandidates':
        return false;
      default:
        return true;
    }
  });
}
